import { useEffect, useState, useMemo } from "react";
import { Sparkles, X, ChevronRight } from "lucide-react";
import { CHARACTERS } from "@/game/config";
import { useLang } from "@/i18n";

const ASSET_BASE = `${process.env.PUBLIC_URL || ""}/game-assets`;

// Dedicated popup portrait — independent of the main-page CharacterPortrait.
// Every sprite is scaled to the SAME height (contain), bottom-aligned, full head always visible.
// The whole card gently bounces so nothing ever clips.
const PopupPortrait = ({ c }) => (
  <div
    className="pixel-card relative overflow-hidden flex items-end justify-center w-full float-up"
    style={{ aspectRatio: "3 / 4", background: `linear-gradient(160deg, ${c.colors.accent}, #111)` }}
    data-testid={`portrait-${c.id}`}
  >
    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "repeating-linear-gradient(45deg,#fff 0 6px,transparent 6px 14px)" }} />
    <img
      src={`${ASSET_BASE}/${c.spriteId || (c.variants && c.variants[0].sprite) || c.id}.webp`}
      alt={c.name}
      draggable={false}
      style={{ height: "90%", width: "94%", objectFit: "cover", objectPosition: "center top", filter: "drop-shadow(2px 4px 4px rgba(0,0,0,0.5))" }}
    />
  </div>
);


const WINDOW = 4;       // portraits visible at once
const ROTATE_MS = 3400; // how often the featured window advances (slower cycle)

// Controlled spotlight on the latest playable characters. Open/close is driven
// by the parent (auto every 12h + a manual home-screen button). The featured
// portraits cycle through every `isNew` character while the popup is open.
export default function NewCharsPopup({ open, onClose, onView, onFishing }) {
  const { t, lang } = useLang();
  const [offset, setOffset] = useState(0);

  // Pool: all Special Guests + any character flagged NEW (deduped), cycled.
  // Feature the newest invited character (Gordan) at the front of the rotation.
  const pool = useMemo(() => {
    const list = CHARACTERS.filter((c) => c.guest || c.isNew);
    const order = { chasedawson: -2, gordankemash: -1 };
    return list.sort((a, b) => (order[a.id] || 0) - (order[b.id] || 0));
  }, []);
  const featured = useMemo(() => {
    if (pool.length === 0) return [];
    const n = Math.min(WINDOW, pool.length);
    return Array.from({ length: n }, (_, i) => pool[(offset + i) % pool.length]);
  }, [pool, offset]);

  // Cycle the featured window while the popup is open.
  useEffect(() => {
    if (!open || pool.length <= WINDOW) return;
    const iv = setInterval(() => setOffset((o) => (o + 1) % pool.length), ROTATE_MS);
    return () => clearInterval(iv);
  }, [open, pool.length]);

  if (!open || pool.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[98] flex items-start justify-center bg-black/80 p-3 sm:p-4 overflow-y-auto overscroll-contain" data-testid="newchars-popup">
      <div className="pixel-card bg-[#141414] w-full max-w-sm p-4 sm:p-6 relative winner-pop text-center my-auto max-h-full overflow-y-auto">
        <button data-testid="newchars-dismiss" onClick={onClose} aria-label={t("newCharsLater")}
          className="absolute top-3 right-3 text-white/50 hover:text-white"><X size={20} /></button>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF1744] text-white font-pixel text-[10px] mb-3 animate-pulse">
          <Sparkles size={13} strokeWidth={3} /> {t("newBadge")}
        </div>
        <h3 className="font-pixel text-sm sm:text-base text-[#FFEB3B] pixel-stroke leading-snug" data-testid="newchars-title">{t("newCharsTitle")}</h3>

        {/* NicaBros Fishing — top spotlight, click straight through to the game */}
        <button
          type="button"
          data-testid="newchars-fishing-cta"
          onClick={() => (onFishing ? onFishing() : onClose())}
          className="group mt-4 w-full rounded-xl overflow-hidden relative text-left transition-transform hover:scale-[1.02] active:scale-[0.99]"
          style={{ background: "linear-gradient(135deg,#00ACC1 0%,#006978 100%)", boxShadow: "0 8px 22px rgba(0,172,193,0.35)" }}
        >
          <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "repeating-linear-gradient(45deg,#fff 0 6px,transparent 6px 14px)" }} />
          <div className="relative flex items-center gap-3 p-3">
            <div className="text-3xl shrink-0">🎣</div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-pixel text-[8px] bg-[#FFEB3B] text-black px-1.5 py-0.5 rounded">{lang === "es" ? "NUEVO" : "NEW"}</span>
                <span className="font-pixel text-[10px] text-white pixel-stroke">NicaBros Fishing</span>
              </div>
              <p className="font-vt text-sm text-white/90 mt-1 leading-tight">{lang === "es" ? "¡Ya podés jugar! Tocá para pescar 🐟" : "It's live! Tap to start fishing 🐟"}</p>
            </div>
            <ChevronRight size={22} className="text-white shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
        </button>

        <div className="flex justify-center gap-1.5 mt-5" data-testid="newchars-portraits">
          {featured.map((c) => (
            <div key={c.id} className="w-1/4 max-w-[78px] newchar-slot">
              <PopupPortrait c={c} />
              <p className="font-vt text-[10px] text-white/85 mt-1 leading-tight truncate" data-testid={`newchar-name-${c.id}`}>{c.name}</p>
            </div>
          ))}
        </div>

        <p className="font-vt text-base text-white/85 mt-4 leading-snug">{t("newCharsBody")}</p>

        <div className="mt-4 pt-3 border-t border-white/10" data-testid="newchars-levels">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF7043] text-black font-pixel text-[8px] mb-2.5">
            <Sparkles size={11} strokeWidth={3} /> {t("newLevelTitle")}
          </div>
          <div className="flex justify-center gap-2">
            {[
              { img: "rodeo_level_card.webp", label: t("rodeoLevelCardName", "Corrida de Toros"), tag: `${t("level")} 7`, testid: "new-level-corrida" },
              { img: "bonus_rodeo_card.webp", label: t("rodeoBonusName", "Monta el Toro"), tag: "BONUS", testid: "new-level-monta" },
            ].map((lv) => (
              <div key={lv.testid} data-testid={lv.testid} className="w-1/2 max-w-[130px]">
                <div className="pixel-card relative overflow-hidden" style={{ aspectRatio: "4 / 3" }}>
                  <img src={`${ASSET_BASE}/${lv.img}`} alt={lv.label} draggable={false} className="absolute inset-0 w-full h-full object-cover" />
                  <span className="absolute top-1 left-1 font-pixel text-[6px] bg-[#FF1744] text-white px-1 py-0.5 rounded pixel-stroke">{lv.tag}</span>
                </div>
                <p className="font-vt text-[11px] text-white/85 mt-1 leading-tight truncate">{lv.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-center gap-2" data-testid="newchars-levels-note">
          <span className="font-vt text-sm text-white/60">{lang === "es" ? "Y más niveles y personajes en camino…" : "Plus more levels & characters on the way…"}</span>
        </div>
        <button data-testid="newchars-later" onClick={onClose}
          className="pixel-btn bg-[#FFB300] text-black w-full py-3 text-xs mt-4">{lang === "es" ? "¡Entendido!" : "Got it!"}</button>
      </div>
    </div>
  );
}
